/* ==========================================================================
   RD: area unit switch

   The client asked for a m2 / sq ft switch "wherever surface area is
   displayed", with the choice staying consistent across the page.

   Areas are written all over the site: product cards, spec tables, the model
   finder, the range copy, FAQ answers. Tagging each one at the source would
   mean touching every section and every template, and would still miss the
   figures the finder renders at runtime. So this walks text nodes instead,
   wraps each area it finds, and can then re-render every one of them from a
   single stored preference.

   Deliberately NOT converted:
     - anything in mm (pipe diameters are a different quantity)
     - script, style, textarea, and the toggle's own labels
     - anything already wrapped, so a re-scan is idempotent
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'rd-area-unit';
  var SQFT_PER_M2 = 10.7639;
  var NBSP = ' ';
  // 1,250 or 50 or 2,500, optionally a range, optionally a space before m²
  var AREA = /(\d{1,3}(?:,\d{3})*|\d+)(\s*[–—-]\s*(\d{1,3}(?:,\d{3})*|\d+))?[  ]?m²/g;
  var SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, OPTION: 1 };

  function readUnit() {
    try {
      var v = window.localStorage.getItem(KEY);
      return v === 'sqft' ? 'sqft' : 'm2';
    } catch (e) {
      return 'm2';
    }
  }

  function writeUnit(unit) {
    try {
      window.localStorage.setItem(KEY, unit);
    } catch (e) {
      /* private mode: the choice simply does not persist */
    }
  }

  function group(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Coverage figures are nominal, so a converted one should read as a round
     number rather than 538.195. */
  function toSqft(m2) {
    var v = m2 * SQFT_PER_M2;
    var step = v >= 2000 ? 50 : 10;
    return group(Math.round(v / step) * step);
  }

  function wrap(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf('m²') < 0) {
          return NodeFilter.FILTER_REJECT;
        }
        var el = node.parentNode;
        while (el && el !== root) {
          if (el.nodeType === 1) {
            if (SKIP[el.nodeName]) return NodeFilter.FILTER_REJECT;
            if (el.classList && el.classList.contains('rd-unit')) return NodeFilter.FILTER_REJECT;
            if (el.hasAttribute && el.hasAttribute('data-rd-no-units')) {
              return NodeFilter.FILTER_REJECT;
            }
          }
          el = el.parentNode;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    var targets = [];
    var node;
    while ((node = walker.nextNode())) targets.push(node);

    targets.forEach(function (text) {
      var value = text.nodeValue;
      AREA.lastIndex = 0;
      if (!AREA.test(value)) return;
      AREA.lastIndex = 0;

      var frag = document.createDocumentFragment();
      var last = 0;
      var m;
      while ((m = AREA.exec(value))) {
        if (m.index > last) {
          frag.appendChild(document.createTextNode(value.slice(last, m.index)));
        }
        var span = document.createElement('span');
        span.className = 'rd-unit';
        span.setAttribute('data-rd-m2', m[0]);
        var lo = parseFloat(m[1].replace(/,/g, ''));
        var hi = m[3] ? parseFloat(m[3].replace(/,/g, '')) : null;
        span.setAttribute(
          'data-rd-sqft',
          (hi === null ? toSqft(lo) : toSqft(lo) + '–' + toSqft(hi)) + NBSP + 'sq ft'
        );
        span.textContent = m[0];
        frag.appendChild(span);
        last = m.index + m[0].length;
      }
      if (last < value.length) {
        frag.appendChild(document.createTextNode(value.slice(last)));
      }
      if (text.parentNode) text.parentNode.replaceChild(frag, text);
    });
  }

  function paint(unit) {
    var spans = document.querySelectorAll('.rd-unit');
    for (var i = 0; i < spans.length; i++) {
      spans[i].textContent = spans[i].getAttribute(
        unit === 'sqft' ? 'data-rd-sqft' : 'data-rd-m2'
      );
    }
    var buttons = document.querySelectorAll('[data-rd-unit-btn]');
    for (var j = 0; j < buttons.length; j++) {
      var on = buttons[j].getAttribute('data-rd-unit-btn') === unit;
      buttons[j].setAttribute('aria-pressed', on ? 'true' : 'false');
      buttons[j].classList.toggle('is-on', on);
    }
  }

  var current = readUnit();

  function set(unit) {
    current = unit === 'sqft' ? 'sqft' : 'm2';
    writeUnit(current);
    paint(current);
    /* The model finder's area FIELD is an <input>, not a text node, so this
       walker can never reach it. It listens for this instead, and converts
       what the visitor typed rather than leaving 180 to mean 180 sq ft. */
    try {
      document.dispatchEvent(
        new CustomEvent('rd:unitchange', { detail: { unit: current } })
      );
    } catch (e) {
      /* no CustomEvent: the switch still works everywhere else */
    }
  }

  function scan() {
    wrap(document.body);
    paint(current);
  }

  function init() {
    scan();

    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-rd-unit-btn]') : null;
      if (!btn) return;
      e.preventDefault();
      set(btn.getAttribute('data-rd-unit-btn'));
    });

    /* The model finder rewrites its result card at runtime, so anything it
       prints has to be picked up after the fact. */
    var finder = document.querySelector('[data-rd-selector]');
    if (finder && window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () {
          pending = null;
          wrap(finder);
          paint(current);
        }, 60);
      }).observe(finder, { childList: true, subtree: true, characterData: true });
    }

    window.rdUnits = { set: set, refresh: scan, get: function () { return current; } };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   RD: Bucks QAR money format (added 2026-10-20)

   A separate job from the area switch above, kept in this file only because
   it already loads on every page (the header-group shim), so no editor-owned
   section group has to change.

   The Bucks currency converter ships its own format table, and it writes the
   Qatari riyal as "QAR {{amount_with_comma_separator}}", i.e. "QAR 4.321,42":
   a full stop for thousands and a comma for decimals. A Gulf buyer reads that
   as about four riyals. Both QAR formats become "QAR {{amount}}", which
   renders "QAR 4,321.42". Every other currency is left alone: QAR is the only
   Gulf or Middle East entry in the comma style (AED, SAR "{{amount}} SR",
   KWD, BHD, OMR, USD and GBP already use a decimal point), and the comma
   style is normal for EUR.

   How this reaches the converter, read from /apps/buckscc/sdk.min.js:
     - window.bucksCC.currencyFormats IS the table the converter reads at
       conversion time, so changing QAR in place changes every later
       conversion, including the model finder's reConvert() after a click.
     - window.bucksCC.customFormats[code] wins over that table when present
       (Bucks fills it from custom-rate currency rules), so a QAR entry there
       in the same comma style is corrected too. One in any other style was
       set deliberately in the Bucks dashboard and is left alone.
     - Bucks fires "BUCKSCC-LOADED" on window just before its first
       conversion. Its SDK is injected late by widgetLoader.js, so this file
       normally runs first and the listener patches the table before any QAR
       price is written. If Bucks got there first, the poll finds it instead.
     - reConvert() skips every price whose bucks-currency attribute already
       names the selected currency, so a price already written in the old QAR
       format is released from that marker first. The converter then rebuilds
       it from the AED amount it cached on the element.
   Everything is guarded: with Bucks absent, renamed or failing, this does
   nothing and the page is unaffected.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__rdBucksQarFormat) return;
  window.__rdBucksQarFormat = true;

  var CODE = 'QAR';
  var FORMAT = 'QAR {{amount}}';
  var KEYS = ['money_format', 'money_with_currency_format'];
  var POLL_MS = 250;
  var POLL_TRIES = 40; // about 10 seconds

  function isCommaStyle(entry) {
    return KEYS.some(function (k) {
      return typeof entry[k] === 'string' && entry[k].indexOf('comma_separator') !== -1;
    });
  }

  function fixEntry(entry, onlyIfCommaStyle) {
    if (!entry || typeof entry !== 'object') return;
    if (onlyIfCommaStyle && !isCommaStyle(entry)) return;
    KEYS.forEach(function (k) {
      entry[k] = FORMAT;
    });
  }

  function selectedCurrency() {
    try {
      var v = window.sessionStorage.getItem('buckscc_customer_currency');
      return v ? JSON.parse(v) : null;
    } catch (e) {
      return null;
    }
  }

  /* Re-render prices that were already written in QAR before the table was
     corrected. Nothing to do when none were, which is the usual case. */
  function refresh(cc) {
    if (typeof cc.reConvert !== 'function') return;
    var stale = document.querySelectorAll('[bucks-currency="' + CODE + '"]');
    if (!stale.length) return;
    var current = selectedCurrency();
    if (current && current !== CODE) return;
    for (var i = 0; i < stale.length; i++) {
      stale[i].removeAttribute('bucks-currency');
    }
    cc.reConvert();
  }

  /* Returns true once Bucks is present, whether or not anything changed. */
  function apply() {
    var cc = window.bucksCC;
    if (!cc || !cc.currencyFormats || typeof cc.currencyFormats !== 'object') return false;
    try {
      fixEntry(cc.currencyFormats[CODE], false);
      if (cc.customFormats && typeof cc.customFormats === 'object') {
        fixEntry(cc.customFormats[CODE], true);
      }
      refresh(cc);
    } catch (e) {}
    return true;
  }

  if (apply()) return;

  /* The poll gives up after about 10 seconds; the event listener stays until
     Bucks loads, however late. */
  var tries = 0;
  var timer = null;

  function stopPolling() {
    if (timer) window.clearInterval(timer);
    timer = null;
  }

  function done() {
    stopPolling();
    window.removeEventListener('BUCKSCC-LOADED', onLoaded);
  }

  function onLoaded() {
    if (apply()) done();
  }

  window.addEventListener('BUCKSCC-LOADED', onLoaded);
  timer = window.setInterval(function () {
    tries += 1;
    if (apply()) done();
    else if (tries >= POLL_TRIES) stopPolling();
  }, POLL_MS);
})();
